// The command palette, as in Sumatra File Manager: one box to type in and a
// list under it. The first character typed picks the list.

export type PaletteMode = "commands" | "history" | "favorites";

export interface PaletteModeDef {
  prefix: string;
  mode: PaletteMode;
  name: string;
}

// the first one is the list shown when no prefix is typed
export const paletteModes: PaletteModeDef[] = [
  { prefix: ">", mode: "commands", name: "Commands" },
  { prefix: "#", mode: "history", name: "History" },
  { prefix: "$", mode: "favorites", name: "Favorites" },
];

export interface PaletteItem {
  // what the words typed are looked for in
  label: string;
  // the label as HTML, if it is more than plain text
  html?: string;
  // shown on the right: a shortcut or a note
  hint?: string;
  run: () => void;
  // there for a row that Del can remove
  remove?: () => void;
}

export type PaletteLists = Record<PaletteMode, PaletteItem[]>;

// Returns the list a query is for and the words to look for in it.
export function paletteParse(query: string): { mode: PaletteMode; text: string } {
  for (let def of paletteModes) {
    if (query.startsWith(def.prefix)) {
      return { mode: def.mode, text: query.substring(def.prefix.length).trim() };
    }
  }
  return { mode: paletteModes[0].mode, text: query.trim() };
}

// Returns the prefix that opens the palette on a list when typed outside of
// a text box, or "" if the key is not one.
export function palettePrefixOfKey(key: string): string {
  for (let def of paletteModes) {
    if (key == def.prefix) {
      return def.prefix;
    }
  }
  return "";
}

// Returns the rows that have every word of the text in their label. Letter
// case does not matter. All rows for an empty text.
export function paletteFilter<T extends { label: string }>(items: T[], text: string): T[] {
  let words = text
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w != "");
  if (words.length == 0) {
    return items;
  }
  return items.filter((item) => {
    let label = item.label.toLowerCase();
    return words.every((w) => label.includes(w));
  });
}

const pageSize = 10;

// Returns the row selected after a key, or -1 if the key does not move the
// selection. Up and Down go around at the ends, Page Up and Page Down stop
// at them.
export function paletteMove(idx: number, n: number, key: string): number {
  if (key != "ArrowUp" && key != "ArrowDown" && key != "PageUp" && key != "PageDown") {
    return -1;
  }
  if (n == 0) {
    return 0;
  }
  switch (key) {
    case "ArrowUp":
      return idx <= 0 ? n - 1 : idx - 1;
    case "ArrowDown":
      return idx >= n - 1 ? 0 : idx + 1;
    case "PageUp":
      return Math.max(0, idx - pageSize);
  }
  return Math.min(n - 1, idx + pageSize);
}

export interface PaletteHint {
  key: string;
  desc: string;
}

// The short help at the bottom of the palette.
export function paletteHints(mode: PaletteMode): PaletteHint[] {
  let hints: PaletteHint[] = [];
  switch (mode) {
    case "commands":
      hints.push({ key: "Enter", desc: "run command" });
      break;
    case "history":
      hints.push({ key: "Enter", desc: "go to" });
      break;
    case "favorites":
      hints.push({ key: "Enter", desc: "go to favorite" });
      hints.push({ key: "Del", desc: "remove favorite" });
      break;
  }
  hints.push({ key: "Esc", desc: "close" });
  return hints;
}

type KeyLike = { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean };

// Ctrl + K, on a Mac Cmd + K.
export function isPaletteShortcut(ev: KeyLike, isMac: boolean): boolean {
  if (ev.altKey || ev.shiftKey || ev.key.toLowerCase() != "k") {
    return false;
  }
  return isMac ? ev.metaKey && !ev.ctrlKey : ev.ctrlKey && !ev.metaKey;
}

export function paletteShortcutName(isMac: boolean): string {
  return isMac ? "Cmd + K" : "Ctrl + K";
}

const entities: Record<string, string> = {
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&amp;": "&",
};

// Returns the text of a label written as HTML, like the ones of menu items:
// without the tags and with one space where there were several.
export function htmlLabelToText(html: string): string {
  let s = html.replace(/<[^>]*>/g, "");
  s = s.replace(/&(lt|gt|quot|#39|amp);/g, (m) => entities[m]);
  return s.replace(/\s+/g, " ").trim();
}
